type SentryModule = typeof import('@sentry/browser')

declare global {
  interface Window {
    __FF_SENTRY__?: {
      enabled: boolean
      dsn: string
    }
  }
}

let sentryModulePromise: Promise<SentryModule | null> | null = null

function getSentryModule(): Promise<SentryModule | null> {
  if (sentryModulePromise) return sentryModulePromise

  const config = window.__FF_SENTRY__
  if (!config?.enabled || !config.dsn) {
    sentryModulePromise = Promise.resolve(null)
    return sentryModulePromise
  }

  sentryModulePromise = import('@sentry/browser').then((Sentry) => {
    if (!Sentry.getClient()) {
      Sentry.init({
        dsn: config.dsn,
        environment: 'production',
        sampleRate: 0.1,
        tracesSampleRate: 0,
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          /^Non-Error promise rejection captured with value: Object$/,
          /Java object is gone/,
          /Failed to fetch \(monorail-edge/,
          /Failed to fetch \(otlp-http-production/,
          /Failed to fetch dynamically imported module:.*shopifycloud\/shop-js/,
          /Request timeout .*Distributor/,
          /Request timeout getDictionariesByLanguageId/,
          /Invalid call to runtime\.sendMessage/,
        ],
        denyUrls: [
          /cdn\/shopifycloud\/privacy-banner/,
          /cdn\/shopifycloud\/storefront\/assets\/shop_events_listener/,
          /cdn\/shopifycloud\/shop-js/,
          /cdn\/shopifycloud\/perf-kit/,
          /cdn\/shopifycloud\/web-pixels-manager/,
          /cdn\/wpm\//,
          /web-pixels@/,
          /cdn\/s\/trekkie/,
          /event-observer-collector/,
          /monorail-edge/,
          /chrome-extension:\/\//,
        ],
      })
    }
    return Sentry
  })

  return sentryModulePromise
}

export function scheduleSentryInit(): void {
  const run = () => {
    void getSentryModule()
  }

  if ('requestIdleCallback' in window) {
    requestIdleCallback(run, { timeout: 4000 })
  } else {
    setTimeout(run, 2000)
  }
}

export function initSentry(): void {
  void getSentryModule()
}

export function captureFetchError(
  error: unknown,
  context: { url: string; method?: string },
): void {
  void getSentryModule().then((Sentry) => {
    if (!Sentry?.getClient()) return

    Sentry.withScope((scope) => {
      scope.setTag('fetch.url', context.url)
      if (context.method) scope.setTag('fetch.method', context.method)

      if (error instanceof Error && 'status' in error) {
        scope.setExtra('status', (error as { status: number }).status)
        if ('body' in error) scope.setExtra('body', (error as { body?: unknown }).body)
      }

      Sentry.captureException(error)
    })
  })
}

export function captureError(error: unknown, context?: Record<string, unknown>): void {
  void getSentryModule().then((Sentry) => {
    if (!Sentry?.getClient()) return

    Sentry.withScope((scope) => {
      if (context) {
        for (const [key, value] of Object.entries(context)) {
          scope.setExtra(key, value)
        }
      }
      Sentry.captureException(error)
    })
  })
}
